import { useEffect, useState, type ReactNode } from "react";
import { useSoftphone } from "./SoftphoneProvider";
import { CallScreen } from "./CallScreen";
import { ActiveCallPill } from "./ActiveCallPill";
import { MicDeniedBanner } from "./MicDeniedBanner";
import { ReconnectBanner } from "./ReconnectBanner";

/**
 * App-wide in-call UI controller (CRM-811). Renders the floating call card
 * (expanded) or the pill (minimized) for the current call from any surface, and
 * mounts the mic-permission / reconnect banners. Mounted once inside
 * SoftphoneProvider (see App.tsx).
 */
export function CallOverlay() {
  const { activeCall, status } = useSoftphone();
  const [minimized, setMinimized] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const callId = activeCall?.callId;
  const answeredAt = activeCall?.answeredAt;

  // Each new call opens expanded.
  useEffect(() => {
    setMinimized(false);
  }, [callId]);

  // Tick the in-call duration from the moment the call was answered.
  useEffect(() => {
    if (status !== "in-call" || !answeredAt) {
      setElapsedSeconds(0);
      return;
    }
    const tick = () =>
      setElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - answeredAt.getTime()) / 1000))
      );
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [status, answeredAt]);

  let callUi: ReactNode = null;
  if (activeCall)
    callUi = minimized ? (
      <ActiveCallPill
        elapsedSeconds={elapsedSeconds}
        onExpand={() => setMinimized(false)}
      />
    ) : (
      <CallScreen
        elapsedSeconds={elapsedSeconds}
        onMinimize={() => setMinimized(true)}
      />
    );

  return (
    <>
      {callUi}
      <MicDeniedBanner />
      <ReconnectBanner />
    </>
  );
}
